import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useI18n } from '../i18n'
import {
  useMyFarm, useFarmBilling, useRoster, useYardReminders, useUpdateFarm,
  useInvites, useCreateInvite, useRevokeInvite, useFarmSubscribe, useFarmTickets,
  startFarmCheckout,
} from '../api/hooks'
import type { Farm, FarmRosterEntry, FarmInvite, FarmRole, LiveryPackage } from '@horsehub/shared'
import { ASSIGNABLE_FARM_ROLES, LIVERY_PACKAGES } from '@horsehub/shared'
import { Modal, Input, Textarea, Select, PageSpinner, EmptyState, Spinner } from '../components/ui'
import { fmtDate } from '../lib/format'
import { confirmDelete } from '../lib/confirm'
import { toast } from '../lib/toast'
import { errorMessage } from '../lib/errors'

/** Card payment when Stripe is configured; the mock flow until then. */
async function payForYard(farmId: string, mockSubscribe: () => void, redirectingMsg: string) {
  try {
    const res = await startFarmCheckout(farmId)
    if (res.url) {
      toast.info(redirectingMsg)
      window.location.assign(res.url)
      return
    }
  } catch (e) {
    toast.error(errorMessage(e))
    return
  }
  mockSubscribe()
}

export function FarmDashboard() {
  const { t, locale } = useI18n()
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const { data: farm, isLoading } = useMyFarm()
  const { data: billing } = useFarmBilling(farm?.id)
  const { data: roster, isLoading: rosterLoading } = useRoster(farm?.id)
  const { data: reminders } = useYardReminders(farm?.id)
  const { data: tickets } = useFarmTickets(farm?.id)
  const subscribe = useFarmSubscribe(farm?.id ?? '')
  const [inviting, setInviting] = useState(false)
  const [editing, setEditing] = useState(false)

  // Back from Stripe Checkout: thank or shrug, then clean the URL.
  useEffect(() => {
    const outcome = params.get('checkout')
    if (!outcome) return
    if (outcome === 'success') toast.success(t('billing.paid'))
    if (outcome === 'cancelled') toast.info(t('billing.cancelled'))
    params.delete('checkout')
    setParams(params, { replace: true })
  }, [params, setParams, t])

  if (isLoading) return <PageSpinner />
  if (!farm) {
    nav('/farm/onboarding', { replace: true })
    return null
  }

  const locked = billing?.locked
  const pay = () => payForYard(farm.id, () => subscribe.mutate(), t('billing.redirecting'))

  return (
    <main className="mx-auto max-w-6xl px-5 py-8">
      {/* Header */}
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="grid h-16 w-16 place-items-center overflow-hidden rounded-2xl bg-brand-100 dark:bg-brand-800 text-2xl">
            {farm.avatarUrl ? <img src={farm.avatarUrl} alt="" className="h-full w-full object-cover" /> : '🏡'}
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">{farm.name}</h1>
            <p className="text-brand-600 dark:text-brand-300">
              {[farm.addressLine, farm.city, farm.postcode, farm.country].filter(Boolean).join(', ') || t('farm.dash.title')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button onClick={() => setEditing(true)} className="btn-ghost !px-4 !py-2.5 text-sm">
            ✎ {t('farm.uk.editYard')}
          </button>
          <button disabled={locked} onClick={() => setInviting(true)} className="btn-primary disabled:opacity-50">
            {t('farm.dash.invite')}
          </button>
        </div>
      </div>

      {/* Trial / locked banner */}
      {billing?.plan === 'trial' && !locked && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-brand-100/70 dark:bg-brand-800/50 px-5 py-3 text-sm">
          <span>⏳ {t('farm.dash.trialBanner', { days: billing.daysLeft })}</span>
          <button onClick={pay} disabled={subscribe.isPending} className="font-semibold text-brand-700 dark:text-brand-200 hover:underline">{t('farm.dash.trialCta')}</button>
        </div>
      )}
      {locked && <FarmPaywall onPay={pay} busy={subscribe.isPending} />}

      {/* Yard sections — big, thumb-friendly destinations */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <YardNavCard to="/farm/tasks" icon="✅" label={t('farm.tasks.title')} />
        <YardNavCard to="/farm/invoices" icon="🧾" label={t('farm.invoices.title')} />
        <YardNavCard to="/farm/people" icon="👥" label={t('farm.members.title')} />
        <YardNavCard
          to="/farm/tickets"
          icon="💬"
          label={t('tickets.title')}
          badge={(tickets ?? []).filter((tk) => tk.status === 'open').length || undefined}
        />
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-3 gap-4">
        <Stat label={t('farm.dash.roster')} value={String(farm.horseCount ?? roster?.length ?? 0)} />
        <Stat label={t('farm.dash.ownersLabel')} value={String(farm.memberCount ?? 0)} />
        <Stat label={t('farm.dash.compliance')} value={String((reminders ?? []).length)} />
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        {/* Roster */}
        <div className="lg:col-span-2">
          <h2 className="mb-4 text-xl font-bold">{t('farm.dash.roster')}</h2>
          {rosterLoading ? (
            <div className="grid place-items-center py-10"><Spinner /></div>
          ) : roster && roster.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {roster.map((r) => <RosterCard key={r.horseId} entry={r} />)}
            </div>
          ) : (
            <EmptyState
              icon="🐎"
              title={t('farm.dash.rosterEmpty')}
              body={t('farm.dash.rosterEmptyBody')}
              action={<button disabled={locked} onClick={() => setInviting(true)} className="btn-primary disabled:opacity-50">{t('farm.dash.invite')}</button>}
            />
          )}
        </div>

        {/* Yard contact + reminders */}
        <aside>
          {(farm.phone || farm.emergencyPhone) && (
            <div className="card mb-6 p-5">
              <h2 className="text-sm font-bold uppercase tracking-wide text-brand-500 dark:text-brand-400">{t('farm.uk.contactCard')}</h2>
              {farm.phone && <div className="mt-2 text-sm">📞 {farm.phone}</div>}
              {farm.emergencyPhone && (
                <div className="mt-1 text-sm">
                  🚨 {t('farm.uk.emergency')}: {[farm.emergencyName, farm.emergencyPhone].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          )}
          <h2 className="mb-4 text-xl font-bold">{t('farm.dash.yardReminders')}</h2>
          {reminders && reminders.length > 0 ? (
            <div className="card divide-y divide-brand-100 dark:divide-brand-700/60">
              {reminders.slice(0, 12).map((r) => (
                <div key={r.id} className="flex items-center justify-between p-4">
                  <div>
                    <div className="font-semibold">{r.horseName}</div>
                    <div className="text-sm text-brand-600 dark:text-brand-300">{r.title || r.category}</div>
                  </div>
                  <span className="chip whitespace-nowrap">{fmtDate(r.nextDue, locale)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="card p-6 text-sm text-brand-600 dark:text-brand-300">{t('farm.dash.noReminders')}</div>
          )}
        </aside>
      </div>

      {inviting && <InviteModal farmId={farm.id} onClose={() => setInviting(false)} />}
      {editing && <YardDetailsModal farm={farm} onClose={() => setEditing(false)} />}
    </main>
  )
}

/** Edit the yard's details — address/postcode, who to ring, and livery prices. */
function YardDetailsModal({ farm, onClose }: { farm: Farm; onClose: () => void }) {
  const { t } = useI18n()
  const update = useUpdateFarm(farm.id)
  const [form, setForm] = useState({
    name: farm.name ?? '',
    about: farm.about ?? '',
    addressLine: farm.addressLine ?? '',
    city: farm.city ?? '',
    postcode: farm.postcode ?? '',
    country: farm.country ?? '',
    phone: farm.phone ?? '',
    emergencyName: farm.emergencyName ?? '',
    emergencyPhone: farm.emergencyPhone ?? '',
  })
  // Package prices edited in pounds; stored in pence.
  const [prices, setPrices] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {}
    for (const p of LIVERY_PACKAGES) {
      const pence = farm.packagePrices?.[p]
      out[p] = typeof pence === 'number' && pence > 0 ? String(pence / 100) : ''
    }
    return out
  })
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    const packagePrices: Record<string, number> = {}
    for (const p of LIVERY_PACKAGES) {
      const pounds = Number(prices[p])
      if (prices[p] !== '' && Number.isFinite(pounds) && pounds >= 0) {
        packagePrices[p] = Math.round(pounds * 100)
      }
    }
    await update.mutateAsync({ ...form, packagePrices })
    toast.success(t('farm.uk.saved'))
    onClose()
  }

  return (
    <Modal title={t('farm.uk.editYard')} onClose={onClose}>
      <div className="space-y-4">
        <Input label={t('farm.onboard.name')} value={form.name} onChange={set('name')} required />
        <Textarea label={t('farm.onboard.about')} value={form.about} onChange={set('about')} rows={2} />
        <Input label={t('farm.uk.addressLine')} value={form.addressLine} onChange={set('addressLine')} />
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('farm.onboard.city')} value={form.city} onChange={set('city')} />
          <Input label={t('farm.uk.postcode')} value={form.postcode} onChange={set('postcode')} placeholder="GL54 1AB" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('farm.onboard.country')} value={form.country} onChange={set('country')} />
          <Input label={t('farm.uk.phone')} value={form.phone} onChange={set('phone')} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('farm.uk.emergencyName')} value={form.emergencyName} onChange={set('emergencyName')} />
          <Input label={t('farm.uk.emergencyPhone')} value={form.emergencyPhone} onChange={set('emergencyPhone')} />
        </div>

        {/* Livery package prices — feed the invoice builder */}
        <div className="border-t border-brand-100 dark:border-brand-700/60 pt-4">
          <h3 className="font-bold">{t('farm.packages.prices')}</h3>
          <p className="mt-1 text-sm text-brand-600 dark:text-brand-300">{t('farm.packages.pricesHint')}</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {LIVERY_PACKAGES.map((p: LiveryPackage) => (
              <label key={p} className="block">
                <span className="mb-1 block text-sm font-medium text-brand-700 dark:text-brand-200">{t(`farm.packages.${p}`)}</span>
                <div className="flex items-center gap-1">
                  <span className="text-brand-600 dark:text-brand-300">£</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    inputMode="decimal"
                    value={prices[p]}
                    placeholder="—"
                    onChange={(e) => setPrices((pr) => ({ ...pr, [p]: e.target.value }))}
                    className="w-full rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-300/40"
                  />
                  <span className="whitespace-nowrap text-sm text-brand-600 dark:text-brand-300">{t('farm.packages.perMonthShort')}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={update.isPending || !form.name.trim()} className="btn-primary w-full disabled:opacity-60">
          {update.isPending ? t('common.save') + '…' : t('farm.uk.save')}
        </button>
      </div>
    </Modal>
  )
}

/** Big tappable card into a yard section — ≥44px targets for gloved thumbs. */
function YardNavCard({ to, icon, label, badge }: { to: string; icon: string; label: string; badge?: number }) {
  return (
    <Link to={to} className="card relative flex items-center gap-3 p-4 transition hover:shadow-lift">
      <span className="text-2xl" aria-hidden>{icon}</span>
      <span className="font-bold">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="absolute right-3 top-3 grid h-6 min-w-6 place-items-center rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white">{badge}</span>
      )}
    </Link>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-5">
      <div className="truncate text-sm text-brand-500 dark:text-brand-400">{label}</div>
      <div className="mt-1 text-2xl font-extrabold">{value}</div>
    </div>
  )
}

function RosterCard({ entry }: { entry: FarmRosterEntry }) {
  const { t } = useI18n()
  const due = entry.shares.reminders ? entry.dueReminders : 0
  return (
    <Link to={`/farm/horse/${entry.horseId}`} className="card flex items-center gap-4 p-4 transition hover:shadow-lift">
      <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-xl bg-brand-100 dark:bg-brand-800 text-xl">
        {entry.profilePhotoUrl ? <img src={entry.profilePhotoUrl} alt="" className="h-full w-full object-cover" /> : '🐎'}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold">{entry.horseName}</div>
        <div className="truncate text-sm text-brand-600 dark:text-brand-300">{entry.ownerName}</div>
        {/* Livery package + UK equine-ID compliance at a glance */}
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs font-semibold">
          {entry.liveryPackage && <span className="chip !px-2 !py-0.5">{t(`farm.packages.${entry.liveryPackage}`)}</span>}
          {entry.stableLocation && <span className="chip !px-2 !py-0.5">📍 {entry.stableLocation}</span>}
          {entry.shares.info && (entry.hasMicrochip !== undefined || entry.hasPassport !== undefined) && (
            <>
              <span className={entry.hasMicrochip ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}>
                {entry.hasMicrochip ? '✓' : '⚠'} {t('farm.uk.microchip')}
              </span>
              <span className={entry.hasPassport ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}>
                {entry.hasPassport ? '✓' : '⚠'} {t('farm.uk.passport')}
              </span>
            </>
          )}
        </div>
      </div>
      {entry.shares.reminders && (
        due > 0
          ? <span className="chip !bg-amber-100 !text-amber-700 dark:!bg-amber-900/40 dark:!text-amber-300 whitespace-nowrap">{t('farm.dash.dueSoon', { n: due })}</span>
          : <span className="chip !bg-emerald-100 !text-emerald-700 dark:!bg-emerald-900/40 dark:!text-emerald-300 whitespace-nowrap">{t('farm.dash.allGood')}</span>
      )}
    </Link>
  )
}

function FarmPaywall({ onPay, busy }: { onPay: () => void; busy: boolean }) {
  const { t } = useI18n()
  return (
    <div id="billing" className="mt-6 card border-2 border-brand-300/70 dark:border-brand-600/60 p-6">
      <div className="text-3xl">🔒</div>
      <h2 className="mt-2 text-xl font-bold">{t('farm.billing.title')}</h2>
      <p className="mt-1 text-sm text-brand-600 dark:text-brand-300">{t('farm.billing.lockedBody')}</p>
      <div className="mt-4 text-3xl font-extrabold">{t('farm.billing.price')}</div>
      <button onClick={onPay} disabled={busy} className="btn-primary mt-4 disabled:opacity-60">
        {busy ? '…' : t('farm.billing.subscribe')}
      </button>
      <p className="mt-2 text-xs text-brand-400">{t('farm.billing.mockNote')}</p>
    </div>
  )
}

function InviteModal({ farmId, onClose }: { farmId: string; onClose: () => void }) {
  const { t } = useI18n()
  const { data: invites } = useInvites(farmId)
  const create = useCreateInvite(farmId)
  const revoke = useRevokeInvite(farmId)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<FarmRole>('boarder')
  const [err, setErr] = useState('')
  const [copied, setCopied] = useState<string | null>(null)

  const send = async () => {
    if (!email.trim()) {
      setErr(t('errors.required', { fields: t('farm.invites.email') }))
      return
    }
    setErr('')
    await create.mutateAsync({ email: email.trim(), invitedName: name.trim() || undefined, role })
    setEmail('')
    setName('')
  }
  const copy = (inv: FarmInvite) => {
    if (inv.url) {
      navigator.clipboard?.writeText(inv.url)
      setCopied(inv.token)
      setTimeout(() => setCopied(null), 1500)
    }
  }

  return (
    <Modal title={t('farm.invites.title')} onClose={onClose}>
      <div className="space-y-4">
        <Input label={t('farm.invites.email')} value={email} onChange={setEmail} placeholder={t('farm.invites.emailPlaceholder')} type="email" />
        <Input label={t('farm.invites.name')} value={name} onChange={setName} />
        <Select
          label={t('farm.invites.role')}
          value={role}
          onChange={(v) => setRole(v as FarmRole)}
          options={ASSIGNABLE_FARM_ROLES.map((r) => ({ value: r, label: t(`farm.roles.${r}`) }))}
        />
        {err && <p className="text-sm font-semibold text-red-600 dark:text-red-400">{err}</p>}
        <button onClick={send} disabled={create.isPending} className="btn-primary w-full disabled:opacity-60">
          {create.isPending ? t('farm.invites.sending') : t('farm.invites.send')}
        </button>
        <p className="text-xs text-brand-500 dark:text-brand-400">{t('farm.invites.note')}</p>

        {invites && invites.length > 0 && (
          <ul className="divide-y divide-brand-100 dark:divide-brand-700/60 border-t border-brand-100 dark:border-brand-700/60 pt-2">
            {invites.map((inv) => (
              <li key={inv.token} className="flex items-center justify-between gap-2 py-2.5 text-sm">
                <div className="min-w-0">
                  <div className="truncate font-medium">{inv.email}</div>
                  <span className={`text-xs ${inv.status === 'accepted' ? 'text-emerald-600 dark:text-emerald-400' : 'text-brand-500'}`}>
                    {inv.status === 'accepted' ? t('farm.invites.accepted') : t('farm.invites.pending')}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {inv.status !== 'accepted' && (
                    <button onClick={() => copy(inv)} className="text-xs font-semibold text-brand-700 dark:text-brand-200 hover:underline">
                      {copied === inv.token ? t('farm.invites.copied') : t('farm.invites.copyLink')}
                    </button>
                  )}
                  <button onClick={() => confirmDelete(t).then((ok) => ok && revoke.mutate(inv.token))} className="text-xs text-red-600 hover:underline dark:text-red-400">
                    {t('farm.invites.revoke')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  )
}
